import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { motion } from 'framer-motion';
import { SunIcon, MoonIcon, StarIcon, SparklesIcon, CalendarIcon, ClockIcon, MapPinIcon, HeartIcon, BriefcaseIcon, CompassIcon, ShieldIcon, ZapIcon, TrendingUpIcon, BookOpenIcon, TargetIcon, CheckIcon, ActivityIcon, LayersIcon, UsersIcon, AlertTriangleIcon, MessageCircleIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { SOLAR_INTRO, SOLAR_YEAR_THEME, SOLAR_ASCENDANT, SOLAR_SUN_POSITION, SOLAR_MOON, SOLAR_PLANETS_IN_HOUSES, SOLAR_ASPECTS, SOLAR_LIFE_AREAS, SOLAR_RECOMMENDATIONS, SOLAR_SUMMARY } from '../../data/solarReportContent';
import { COMPAT_INTRO, COMPAT_OVERALL_DYNAMICS, COMPAT_SUN_INTERACTION, COMPAT_EMOTIONAL, COMPAT_ROMANTIC, COMPAT_INTELLECTUAL, COMPAT_KARMIC, COMPAT_PLANETS_IN_HOUSES, COMPAT_ASPECTS_DETAILED, COMPAT_STRENGTHS, COMPAT_CONFLICTS, COMPAT_LONGTERM, COMPAT_RECOMMENDATIONS, COMPAT_SUMMARY } from '../../data/compatibilityReportContent';
import { COMBO_NATAL_INTRO, COMBO_PERSONALITY, COMBO_SUN, COMBO_MOON, COMBO_ASCENDANT, COMBO_PLANETS_IN_SIGNS, COMBO_HOUSES_SUMMARY, COMBO_NATAL_ASPECTS, COMBO_NATAL_LIFE_AREAS, COMBO_SOLAR_INTRO, COMBO_YEAR_ENERGY, COMBO_SOLAR_ASCENDANT, COMBO_SOLAR_SUN, COMBO_SOLAR_MOON, COMBO_SOLAR_PLANETS, COMBO_SOLAR_ASPECTS, COMBO_KEY_PERIODS, COMBO_SOLAR_NATAL_INTERACTION, COMBO_YEAR_LIFE_AREAS, COMBO_RECOMMENDATIONS, COMBO_FINAL_SUMMARY } from '../../data/comboReportContent';
import { FORECAST_INTRO, FORECAST_PERIOD_ENERGY, FORECAST_SLOW_TRANSITS, FORECAST_PERSONAL_TRANSITS, FORECAST_HOUSES_ACTIVATION, FORECAST_TRANSIT_ASPECTS, FORECAST_KEY_PERIODS, FORECAST_LIFE_AREAS, FORECAST_OPPORTUNITIES, FORECAST_DIFFICULTIES, FORECAST_RECOMMENDATIONS, FORECAST_SUMMARY, FORECAST_MONTHLY_DETAIL } from '../../data/forecastReportContent';
import { INTRO_CONTENT, EXTENDED_SUN, EXTENDED_MOON, EXTENDED_MERCURY, EXTENDED_VENUS, EXTENDED_MARS, EXTENDED_JUPITER, EXTENDED_SATURN, OUTER_PLANETS_EXTENDED, HOUSES_DETAILED, ASPECTS_DETAILED, HEALTH_SECTION, KARMIC_SECTION, TALENTS_SECTION, CHALLENGES_SECTION, FORECAST_SECTION, FINAL_ANALYSIS, PLANET_POSITIONS_TABLE, ENERGY_RADAR_DATA, ZODIAC_COMPATIBILITY, PERSONAL_AFFIRMATIONS, CURRENT_TRANSITS, CELEBRITY_MATCHES } from '../../data/fullReportContent';
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast';
interface PremiumReportPreviewProps {
  serviceType: ServiceType;
}
// ═══════════════════════════════════════════
// SERVICE-SPECIFIC DATA
// ═══════════════════════════════════════════
const SERVICE_CONFIG: Record<ServiceType, {
  title: string;
  subtitle: string;
  gradient: string;
  accentColor: string;
  badgeClass: string;
  icon: React.ReactNode;
  person2?: {
    name: string;
    date: string;
    time: string;
    place: string;
  };
  extraInfo?: string;
}> = {
  natal: {
    title: 'Натальная карта',
    subtitle: 'Полный астрологический разбор личности',
    gradient: 'from-slate-900 via-indigo-900 to-slate-900',
    accentColor: 'indigo',
    badgeClass: 'bg-indigo-500/30 text-indigo-200 border-indigo-400/30',
    icon: <StarIcon className="w-8 h-8" data-id="element-16644" />
  },
  solar: {
    title: 'Соляр (Прогноз на год)',
    subtitle: 'Персональный астрологический прогноз на 2026 год',
    gradient: 'from-slate-900 via-amber-900 to-slate-900',
    accentColor: 'amber',
    badgeClass: 'bg-amber-500/30 text-amber-200 border-amber-400/30',
    icon: <SunIcon className="w-8 h-8" data-id="element-16645" />,
    extraInfo: 'Солярный год: 2026'
  },
  compatibility: {
    title: 'Совместимость (Синастрия)',
    subtitle: 'Астрологический анализ отношений',
    gradient: 'from-slate-900 via-rose-900 to-slate-900',
    accentColor: 'rose',
    badgeClass: 'bg-rose-500/30 text-rose-200 border-rose-400/30',
    icon: <HeartIcon className="w-8 h-8" data-id="element-16646" />,
    person2: {
      name: 'Мария Иванова',
      date: '23 марта 1994',
      time: '09:15',
      place: 'Санкт-Петербург'
    }
  },
  combo: {
    title: 'Комбо: Натал + Соляр',
    subtitle: 'Полный анализ личности + прогноз на год',
    gradient: 'from-slate-900 via-purple-900 to-slate-900',
    accentColor: 'purple',
    badgeClass: 'bg-purple-500/30 text-purple-200 border-purple-400/30',
    icon: <LayersIcon className="w-8 h-8" data-id="element-16647" />,
    extraInfo: 'Солярный год: 2026'
  },
  forecast: {
    title: 'Прогноз (Транзиты)',
    subtitle: 'Анализ текущих и предстоящих транзитов',
    gradient: 'from-slate-900 via-teal-900 to-slate-900',
    accentColor: 'teal',
    badgeClass: 'bg-teal-500/30 text-teal-200 border-teal-400/30',
    icon: <ActivityIcon className="w-8 h-8" data-id="element-16648" />,
    extraInfo: 'Период: Март 2026 — Сентябрь 2026'
  }
};
const REPORT_PAGE_COUNT: Record<ServiceType, number> = {
  natal: 35,
  solar: 38,
  compatibility: 32,
  combo: 62,
  forecast: 30
};
const TOC_BY_SERVICE: Record<ServiceType, {
  num: string;
  title: string;
}[]> = {
  natal: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общий психологический портрет'
  }, {
    num: '03',
    title: 'Солнце — Ядро личности'
  }, {
    num: '04',
    title: 'Луна — Внутренний мир'
  }, {
    num: '05',
    title: 'Асцендент — Социальная маска'
  }, {
    num: '06',
    title: 'Меркурий — Мышление'
  }, {
    num: '07',
    title: 'Венера — Любовь и ценности'
  }, {
    num: '08',
    title: 'Марс — Энергия и действие'
  }, {
    num: '09',
    title: 'Планеты в домах'
  }, {
    num: '10',
    title: 'Аспекты между планетами'
  }, {
    num: '11',
    title: 'Карьера и финансы'
  }, {
    num: '12',
    title: 'Любовь и отношения'
  }, {
    num: '13',
    title: 'Кармические задачи'
  }, {
    num: '14',
    title: 'Таланты и предназначение'
  }, {
    num: '15',
    title: 'Итоговый анализ'
  }],
  solar: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая тема года'
  }, {
    num: '03',
    title: 'Асцендент соляра'
  }, {
    num: '04',
    title: 'Положение Солнца в соляре'
  }, {
    num: '05',
    title: 'Луна в соляре'
  }, {
    num: '06',
    title: 'Меркурий в соляре'
  }, {
    num: '07',
    title: 'Венера в соляре'
  }, {
    num: '08',
    title: 'Марс в соляре'
  }, {
    num: '09',
    title: 'Юпитер, Сатурн и внешние планеты'
  }, {
    num: '10',
    title: 'Аспекты соляра'
  }, {
    num: '11',
    title: 'Карьера и финансы'
  }, {
    num: '12',
    title: 'Отношения и семья'
  }, {
    num: '13',
    title: 'Личностное развитие и здоровье'
  }, {
    num: '14',
    title: 'Важные периоды года'
  }, {
    num: '15',
    title: 'Рекомендации на год'
  }, {
    num: '16',
    title: 'Итог прогноза'
  }],
  compatibility: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая динамика отношений'
  }, {
    num: '03',
    title: 'Солнце и личность партнёров'
  }, {
    num: '04',
    title: 'Эмоциональная совместимость'
  }, {
    num: '05',
    title: 'Романтическая и сексуальная совместимость'
  }, {
    num: '06',
    title: 'Интеллектуальная совместимость'
  }, {
    num: '07',
    title: 'Кармические связи'
  }, {
    num: '08',
    title: 'Планеты партнёра в домах другого'
  }, {
    num: '09',
    title: 'Основные аспекты между картами'
  }, {
    num: '10',
    title: 'Сильные стороны отношений'
  }, {
    num: '11',
    title: 'Возможные конфликты'
  }, {
    num: '12',
    title: 'Потенциал долгосрочных отношений'
  }, {
    num: '13',
    title: 'Практические рекомендации'
  }, {
    num: '14',
    title: 'Итог анализа'
  }],
  combo: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая характеристика личности'
  }, {
    num: '03',
    title: 'Солнце — Ядро личности'
  }, {
    num: '04',
    title: 'Луна — Эмоциональная природа'
  }, {
    num: '05',
    title: 'Асцендент — Внешнее проявление'
  }, {
    num: '06',
    title: 'Планеты в знаках'
  }, {
    num: '07',
    title: 'Планеты в домах'
  }, {
    num: '08',
    title: 'Аспекты натальной карты'
  }, {
    num: '09',
    title: 'Сферы жизни (натал)'
  }, {
    num: '10',
    title: 'Введение в соляр'
  }, {
    num: '11',
    title: 'Общая энергия 2026 года'
  }, {
    num: '12',
    title: 'Асцендент соляра'
  }, {
    num: '13',
    title: 'Солнце в соляре'
  }, {
    num: '14',
    title: 'Луна в соляре'
  }, {
    num: '15',
    title: 'Планеты соляра в домах'
  }, {
    num: '16',
    title: 'Аспекты соляра'
  }, {
    num: '17',
    title: 'Важные периоды года'
  }, {
    num: '18',
    title: 'Влияние соляра на натал'
  }, {
    num: '19',
    title: 'Сферы жизни (год)'
  }, {
    num: '20',
    title: 'Рекомендации на год'
  }, {
    num: '21',
    title: 'Итоговый анализ'
  }],
  forecast: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая энергия периода'
  }, {
    num: '03',
    title: 'Медленные транзиты'
  }, {
    num: '04',
    title: 'Транзиты личных планет'
  }, {
    num: '05',
    title: 'Транзиты по домам'
  }, {
    num: '06',
    title: 'Аспекты транзитов'
  }, {
    num: '07',
    title: 'Ключевые периоды'
  }, {
    num: '08',
    title: 'Сферы жизни'
  }, {
    num: '09',
    title: 'Возможности периода'
  }, {
    num: '10',
    title: 'Потенциальные трудности'
  }, {
    num: '11',
    title: 'Рекомендации'
  }, {
    num: '12',
    title: 'Итог прогноза'
  }]
};
// ═══════════════════════════════════════════
// NATAL DATA
// ═══════════════════════════════════════════
const NATAL_ELEMENT_BALANCE = [{
  element: 'Огонь',
  percent: 20,
  color: 'bg-red-500',
  planets: 'Солнце, Меркурий'
}, {
  element: 'Земля',
  percent: 30,
  color: 'bg-emerald-500',
  planets: 'Юпитер, Уран, Нептун'
}, {
  element: 'Воздух',
  percent: 20,
  color: 'bg-blue-500',
  planets: 'Марс, Сатурн'
}, {
  element: 'Вода',
  percent: 30,
  color: 'bg-indigo-500',
  planets: 'Луна, Венера, Плутон'
}];
const NATAL_ASPECTS_GRID = [{
  planets: '☉ ☌ ☿',
  type: 'Соединение',
  nature: 'intense'
}, {
  planets: '☽ △ ♀',
  type: 'Трин',
  nature: 'harmonious'
}, {
  planets: '♀ ⚹ ♃',
  type: 'Секстиль',
  nature: 'harmonious'
}, {
  planets: '♂ □ ♃',
  type: 'Квадрат',
  nature: 'tense'
}, {
  planets: '♂ □ ♄',
  type: 'Квадрат',
  nature: 'tense'
}];
// ═══════════════════════════════════════════
// SOLAR DATA
// ═══════════════════════════════════════════
const SOLAR_MONTHLY_FORECAST = [{
  month: 'Янв',
  intensity: 60,
  theme: 'Планирование'
}, {
  month: 'Фев',
  intensity: 55,
  theme: 'Подготовка'
}, {
  month: 'Мар',
  intensity: 90,
  theme: 'Прорыв'
}, {
  month: 'Апр',
  intensity: 85,
  theme: 'Рост'
}, {
  month: 'Май',
  intensity: 80,
  theme: 'Реализация'
}, {
  month: 'Июн',
  intensity: 65,
  theme: 'Стабилизация'
}, {
  month: 'Июл',
  intensity: 50,
  theme: 'Отдых'
}, {
  month: 'Авг',
  intensity: 70,
  theme: 'Новый цикл'
}, {
  month: 'Сен',
  intensity: 75,
  theme: 'Активность'
}, {
  month: 'Окт',
  intensity: 60,
  theme: 'Завершение'
}, {
  month: 'Ноя',
  intensity: 55,
  theme: 'Рефлексия'
}, {
  month: 'Дек',
  intensity: 65,
  theme: 'Итоги'
}];
const SOLAR_KEY_DATES = [{
  date: '15 марта',
  event: 'Новолуние в 10 доме',
  type: 'positive'
}, {
  date: '28 апреля',
  event: 'Юпитер тригон Солнце',
  type: 'positive'
}, {
  date: '12 июня',
  event: 'Сатурн квадрат Луна',
  type: 'caution'
}, {
  date: '8 августа',
  event: 'День рождения — новый соляр',
  type: 'neutral'
}, {
  date: '22 октября',
  event: 'Затмение в 5 доме',
  type: 'caution'
}, {
  date: '5 декабря',
  event: 'Венера секстиль Юпитер',
  type: 'positive'
}];
// ═══════════════════════════════════════════
// COMPATIBILITY DATA
// ═══════════════════════════════════════════
const COMPATIBILITY_SCORES = [{
  area: 'Эмоциональная связь',
  percent: 85,
  color: 'bg-rose-500'
}, {
  area: 'Романтика и страсть',
  percent: 92,
  color: 'bg-pink-500'
}, {
  area: 'Коммуникация',
  percent: 78,
  color: 'bg-sky-500'
}, {
  area: 'Быт и финансы',
  percent: 70,
  color: 'bg-emerald-500'
}, {
  area: 'Долгосрочные перспективы',
  percent: 88,
  color: 'bg-purple-500'
}];
const COMPATIBILITY_ASPECTS = [{
  aspect: '☉ △ ☽',
  desc: 'Солнце — Луна',
  nature: 'harmonious'
}, {
  aspect: '♀ ☌ ♂',
  desc: 'Венера — Марс',
  nature: 'intense'
}, {
  aspect: '☽ ⚹ ☽',
  desc: 'Луна — Луна',
  nature: 'harmonious'
}, {
  aspect: '☿ □ ☿',
  desc: 'Меркурий — Меркурий',
  nature: 'tense'
}, {
  aspect: '♃ △ ♀',
  desc: 'Юпитер — Венера',
  nature: 'harmonious'
}];
// ═══════════════════════════════════════════
// FORECAST DATA
// ═══════════════════════════════════════════
const FORECAST_ACTIVE_TRANSITS = [{
  planet: 'Юпитер',
  position: 'в 10 доме',
  effect: 'Карьерный рост',
  strength: 90,
  color: 'bg-amber-500'
}, {
  planet: 'Сатурн',
  position: 'в 7 доме',
  effect: 'Уроки в отношениях',
  strength: 75,
  color: 'bg-slate-500'
}, {
  planet: 'Уран',
  position: 'в 1 доме',
  effect: 'Личная трансформация',
  strength: 85,
  color: 'bg-cyan-500'
}, {
  planet: 'Нептун',
  position: 'в 9 доме',
  effect: 'Духовный поиск',
  strength: 60,
  color: 'bg-indigo-500'
}, {
  planet: 'Плутон',
  position: 'в 8 доме',
  effect: 'Глубокие перемены',
  strength: 70,
  color: 'bg-purple-500'
}];
const FORECAST_TIMELINE = [{
  period: 'Март',
  events: ['Юпитер тригон Солнце', 'Новолуние в 10 доме'],
  intensity: 'high'
}, {
  period: 'Апрель',
  events: ['Меркурий ретро', 'Венера в 7 доме'],
  intensity: 'medium'
}, {
  period: 'Май',
  events: ['Затмение в 11 доме', 'Марс секстиль Юпитер'],
  intensity: 'high'
}, {
  period: 'Июнь',
  events: ['Сатурн квадрат Луна'],
  intensity: 'low'
}];
export function PremiumReportPreview({
  serviceType
}: PremiumReportPreviewProps) {
  const config = SERVICE_CONFIG[serviceType];
  const toc = TOC_BY_SERVICE[serviceType];
  // Review state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewTags, setReviewTags] = useState<string[]>([]);
  const [reviewText, setReviewText] = useState('');
  // ═══════════════════════════════════════════
  // RENDER NATAL CONTENT
  // ═══════════════════════════════════════════
  const renderNatalContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle,
      color = 'indigo'
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-16649">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`} data-id="element-16650">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-16651">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-16652">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-16653">{subtitle}</p>}
        </div>
      </div>;
    const renderParagraphs = (text: string) => {
      return text.split('\n\n').map((paragraph, i) => <p key={i} className="text-sm sm:text-base text-slate-600 leading-relaxed mb-3 sm:mb-4 last:mb-0" data-id="element-16654">
          {paragraph}
        </p>);
    };
    return <>
        {/* 01. ВВЕДЕНИЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16655">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-16657" />} title={INTRO_CONTENT.title} subtitle="Что такое натальная карта и как работает астрология" data-id="element-16656" />
          <div className="space-y-6" data-id="element-16658">
            <div data-id="element-16659">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16660">
                Что такое натальная карта?
              </h3>
              {renderParagraphs(INTRO_CONTENT.whatIsNatalChart)}
            </div>
            <div data-id="element-16661">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16662">
                Как это работает?
              </h3>
              {renderParagraphs(INTRO_CONTENT.howItWorks)}
            </div>
            <div className="bg-indigo-50/50 p-5 rounded-xl border border-indigo-100" data-id="element-16663">
              <h3 className="font-bold text-indigo-800 mb-2" data-id="element-16664">
                Как читать этот отчёт
              </h3>
              {renderParagraphs(INTRO_CONTENT.howToRead)}
            </div>
          </div>
        </div>

        {/* 02. ОБЩИЙ ПСИХОЛОГИЧЕСКИЙ ПОРТРЕТ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-16665">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-16667" />} title="Общий психологический портрет" subtitle="Фундамент вашей личности" data-id="element-16666" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-8" data-id="element-16668">
            <Card padding="md" className="bg-white border-amber-200 shadow-sm" data-id="element-16669">
              <div className="flex items-center gap-2 mb-3" data-id="element-16670">
                <SunIcon className="w-5 h-5 text-amber-500" data-id="element-16671" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-16672">
                  Солнце (Эго)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-16673">
                <span className="text-amber-500" data-id="element-16674">♌</span> Лев
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-16675">
                Обитель Солнца • Огненная стихия
              </p>
            </Card>

            <Card padding="md" className="bg-white border-indigo-200 shadow-sm" data-id="element-16676">
              <div className="flex items-center gap-2 mb-3" data-id="element-16677">
                <MoonIcon className="w-5 h-5 text-indigo-500" data-id="element-16678" />
                <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-16679">
                  Луна (Душа)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-16680">
                <span className="text-indigo-500" data-id="element-16681">♏</span> Скорпион
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-16682">
                Падение Луны • Водная стихия
              </p>
            </Card>

            <Card padding="md" className="bg-white border-purple-200 shadow-sm" data-id="element-16683">
              <div className="flex items-center gap-2 mb-3" data-id="element-16684">
                <StarIcon className="w-5 h-5 text-purple-500" data-id="element-16685" />
                <span className="text-xs font-bold text-purple-600 uppercase tracking-wider" data-id="element-16686">
                  Асцендент (Маска)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-16687">
                <span className="text-purple-500" data-id="element-16688">♐</span> Стрелец
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-16689">
                Управитель: Юпитер • Огненная стихия
              </p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-16690">
            <div data-id="element-16691">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-16692">
                Баланс стихий
              </h3>
              <div className="space-y-4" data-id="element-16693">
                {NATAL_ELEMENT_BALANCE.map((item, i) => <div key={i} data-id="element-16694">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-16695">
                      <span className="font-medium text-slate-700" data-id="element-16696">
                        {item.element} ({item.percent}%)
                      </span>
                      <span className="text-slate-400 text-xs" data-id="element-16697">
                        {item.planets}
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-16698">
                      <motion.div className={`${item.color} h-2.5 rounded-full`} initial={{
                    width: 0
                  }} animate={{
                    width: `${item.percent}%`
                  }} transition={{
                    duration: 1,
                    delay: i * 0.15,
                    ease: 'easeOut'
                  }} data-id="element-16699" />
                    </div>
                  </div>)}
              </div>
            </div>

            <div data-id="element-16700">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-16701">
                Энергетический профиль
              </h3>
              <div className="space-y-4" data-id="element-16702">
                {ENERGY_RADAR_DATA.axes.map((item, i) => <div key={i} data-id="element-16703">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-16704">
                      <span className="font-medium text-slate-700" data-id="element-16705">
                        {item.label}
                      </span>
                      <span className="font-bold text-indigo-600" data-id="element-16706">
                        {item.value}/100
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-16707">
                      <motion.div className="bg-indigo-500 h-2.5 rounded-full" initial={{
                    width: 0
                  }} animate={{
                    width: `${item.value}%`
                  }} transition={{
                    duration: 1,
                    delay: i * 0.1,
                    ease: 'easeOut'
                  }} data-id="element-16708" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-16709">
            Положения планет
          </h3>
          <div className="-mx-4 sm:mx-0 overflow-x-auto" data-id="element-16710">
            <table className="w-full text-sm text-left border-collapse min-w-[500px]" data-id="element-16711">
              <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-16712">
                <tr data-id="element-16713">
                  <th className="px-4 py-3 rounded-tl-lg" data-id="element-16714">Планета</th>
                  <th className="px-4 py-3" data-id="element-16715">Знак</th>
                  <th className="px-4 py-3" data-id="element-16716">Градус</th>
                  <th className="px-4 py-3" data-id="element-16717">Дом</th>
                  <th className="px-4 py-3 rounded-tr-lg" data-id="element-16718">Достоинство</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white" data-id="element-16719">
                {PLANET_POSITIONS_TABLE.map((planet, i) => <tr key={i} className="hover:bg-slate-50/50 transition-colors" data-id="element-16720">
                    <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-2" data-id="element-16721">
                      <span className="text-lg text-indigo-500 w-6 text-center" data-id="element-16722">
                        {planet.symbol}
                      </span>
                      {planet.planet}
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-16723">{planet.sign}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs" data-id="element-16724">
                      {planet.degree}
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-16725">{planet.house}</td>
                    <td className="px-4 py-3 text-slate-500" data-id="element-16726">
                      {planet.dignity}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* 03. СОЛНЦЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16727">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-16729" />} title="Солнце во Льве" subtitle="Ядро личности, эго и жизненная сила" color="amber" data-id="element-16728" />
          <div className="space-y-6" data-id="element-16730">
            <div data-id="element-16731">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16732">Жизненная цель</h3>
              {renderParagraphs(EXTENDED_SUN.lifePurpose)}
            </div>
            <div data-id="element-16733">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16734">
                В повседневной жизни
              </h3>
              {renderParagraphs(EXTENDED_SUN.dailyLife)}
            </div>
            <div data-id="element-16735">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16736">В отношениях</h3>
              {renderParagraphs(EXTENDED_SUN.inRelationships)}
            </div>
          </div>
        </div>

        {/* 04. ЛУНА */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-16737">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-16739" />} title="Луна в Скорпионе" subtitle="Внутренний мир, эмоции и подсознание" color="indigo" data-id="element-16738" />
          <div className="space-y-6" data-id="element-16740">
            <div data-id="element-16741">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16742">
                Эмоциональные паттерны
              </h3>
              {renderParagraphs(EXTENDED_MOON.emotionalPatterns)}
            </div>
            <div data-id="element-16743">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16744">
                Отношения с матерью
              </h3>
              {renderParagraphs(EXTENDED_MOON.motherRelation)}
            </div>
            <div className="bg-indigo-50/50 p-5 rounded-xl border border-indigo-100" data-id="element-16745">
              <h3 className="font-bold text-indigo-800 mb-2" data-id="element-16746">
                Скрытые потребности
              </h3>
              {renderParagraphs(EXTENDED_MOON.hiddenNeeds)}
            </div>
          </div>
        </div>

        {/* 05. АСЦЕНДЕНТ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16747">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-16749" />} title="Асцендент в Стрельце" subtitle="Социальная маска и первое впечатление" color="purple" data-id="element-16748" />
          <div className="space-y-4" data-id="element-16750">
            <p className="text-slate-600 leading-relaxed" data-id="element-16751">
              Асцендент в Стрельце формирует ваш внешний образ — оптимистичный,
              открытый и любознательный. Люди воспринимают вас как человека с
              широким кругозором и философским взглядом на жизнь. Вы производите
              впечатление человека, который знает, куда идёт, и приглашает
              других присоединиться к путешествию.
            </p>
            <p className="text-slate-600 leading-relaxed" data-id="element-16752">
              Ваше тело, скорее всего, крупное или высокое, с широкими жестами и
              открытой позой. Вы излучаете энтузиазм и уверенность. Однако за
              этой жизнерадостной маской скрывается глубокий и интенсивный
              внутренний мир (Луна в Скорпионе), который вы открываете далеко не
              всем.
            </p>
          </div>
        </div>

        {/* 06. МЕРКУРИЙ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-sky-50/30 to-white" data-id="element-16753">
          <SectionHeader icon={<MessageCircleIcon className="w-5 h-5" data-id="element-16755" />} title="Меркурий во Льве" subtitle="Мышление, речь и коммуникация" color="sky" data-id="element-16754" />
          <div className="space-y-6" data-id="element-16756">
            <div data-id="element-16757">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16758">Стиль обучения</h3>
              {renderParagraphs(EXTENDED_MERCURY.learningStyle)}
            </div>
            <div data-id="element-16759">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16760">Стиль общения</h3>
              {renderParagraphs(EXTENDED_MERCURY.communicationStyle)}
            </div>
            <div data-id="element-16761">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16762">
                Принятие решений
              </h3>
              {renderParagraphs(EXTENDED_MERCURY.decisionMaking)}
            </div>
          </div>
        </div>

        {/* 07. ВЕНЕРА */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16763">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-16765" />} title="Венера в Раке" subtitle="Любовь, ценности и эстетика" color="rose" data-id="element-16764" />
          <div className="space-y-6" data-id="element-16766">
            <div data-id="element-16767">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16768">Язык любви</h3>
              {renderParagraphs(EXTENDED_VENUS.loveLanguage)}
            </div>
            <div data-id="element-16769">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16770">Эстетика и вкус</h3>
              {renderParagraphs(EXTENDED_VENUS.aesthetics)}
            </div>
            <div data-id="element-16771">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16772">
                Финансовые привычки
              </h3>
              {renderParagraphs(EXTENDED_VENUS.financialHabits)}
            </div>
          </div>
        </div>

        {/* 08. МАРС */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-16773">
          <SectionHeader icon={<ZapIcon className="w-5 h-5" data-id="element-16775" />} title="Марс в Близнецах" subtitle="Энергия, действие и конфликт" color="amber" data-id="element-16774" />
          <div className="space-y-6" data-id="element-16776">
            <div data-id="element-16777">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16778">Стиль конфликта</h3>
              {renderParagraphs(EXTENDED_MARS.conflictStyle)}
            </div>
            <div data-id="element-16779">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16780">
                Амбиции и действия
              </h3>
              {renderParagraphs(EXTENDED_MARS.ambitionStyle)}
            </div>
            <div data-id="element-16781">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16782">
                Физическая энергия
              </h3>
              {renderParagraphs(EXTENDED_MARS.physicalEnergy)}
            </div>
          </div>
        </div>

        {/* 09. ПЛАНЕТЫ В ДОМАХ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16783">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-16785" />} title="Планеты в домах" subtitle="Сферы жизни и где проявляется ваша энергия" color="indigo" data-id="element-16784" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-16786">
            {HOUSES_DETAILED.map((house, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className={`rounded-xl p-5 border ${house.planets.length > 0 ? 'bg-indigo-50/50 border-indigo-200 shadow-sm' : 'bg-slate-50 border-slate-200'}`} data-id="element-16787">
                <div className="flex items-center justify-between mb-3" data-id="element-16788">
                  <div className="flex items-center gap-2" data-id="element-16789">
                    <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${house.planets.length > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-16790">
                      {house.num}
                    </span>
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-16791">
                      {house.theme}
                    </h4>
                  </div>
                  <span className="text-xs font-medium text-slate-500" data-id="element-16792">
                    {house.sign}
                  </span>
                </div>
                {house.planets.length > 0 && <div className="mb-2 flex flex-wrap gap-1" data-id="element-16793">
                    {house.planets.map((p, idx) => <Badge key={idx} variant="pro" className="text-[10px] px-1.5 py-0" data-id="element-16794">
                        {p}
                      </Badge>)}
                  </div>}
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-16795">
                  {house.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* 10. АСПЕКТЫ МЕЖДУ ПЛАНЕТАМИ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-16796">
          <SectionHeader icon={<ActivityIcon className="w-5 h-5" data-id="element-16798" />} title="Аспекты между планетами" subtitle="Внутренние диалоги и конфликты" color="indigo" data-id="element-16797" />
          <div className="space-y-4" data-id="element-16799">
            {ASPECTS_DETAILED.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            const isIntense = aspect.nature === 'intense';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.08
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : isIntense ? 'bg-purple-50/50 border-purple-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-16800">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-16801">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-16802">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : isIntense ? 'text-purple-600' : 'text-rose-600'}`} data-id="element-16803">
                        {aspect.symbol}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-16804">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : isIntense ? 'pro' : 'warning'} className="text-xs" data-id="element-16805">
                      {isHarmonious ? 'Гармоничный' : isIntense ? 'Интенсивный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-16806">
                    {aspect.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* 11. КАРЬЕРА И ФИНАНСЫ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16807">
          <SectionHeader icon={<BriefcaseIcon className="w-5 h-5" data-id="element-16809" />} title="Карьера и финансы" subtitle="Профессиональный потенциал и заработок" color="emerald" data-id="element-16808" />
          <div className="space-y-6" data-id="element-16810">
            <div data-id="element-16811">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16812">
                Профессиональный путь
              </h3>
              {renderParagraphs(EXTENDED_JUPITER)}
            </div>
            <div data-id="element-16813">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16814">
                Финансовая стратегия
              </h3>
              {renderParagraphs(OUTER_PLANETS_EXTENDED.uranus)}
            </div>
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200" data-id="element-16815">
              <h3 className="font-bold text-emerald-800 mb-3" data-id="element-16816">
                Лучшие сферы для реализации
              </h3>
              <ul className="space-y-2" data-id="element-16817">
                {TALENTS_SECTION.bestAreas.split('\n').filter(line => line.trim().startsWith('•')).map((area, i) => <li key={i} className="flex items-start gap-2 text-sm text-emerald-700" data-id="element-16818">
                      <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-16819" />
                      {area.replace('•', '').trim()}
                    </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* 12. ЛЮБОВЬ И ОТНОШЕНИЯ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-16820">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-16822" />} title="Любовь и отношения" subtitle="Совместимость и партнёрство" color="rose" data-id="element-16821" />
          <div className="space-y-6 mb-8" data-id="element-16823">
            <p className="text-slate-600 leading-relaxed" data-id="element-16824">
              Ваш подход к отношениям формируется Венерой в Раке (потребность в
              заботе и уюте), Марсом в Близнецах (потребность в интеллектуальном
              стимулировании) и 7 домом в Близнецах. Вы ищете партнёра, с
              которым можно и глубоко помолчать, и жарко поспорить.
            </p>
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-16825">
            Совместимость со знаками зодиака
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-16826">
            {ZODIAC_COMPATIBILITY.map((sign, i) => <div key={i} className={`p-3 rounded-xl border ${sign.best ? 'bg-rose-50 border-rose-200 shadow-sm' : 'bg-white border-slate-200'}`} data-id="element-16827">
                <div className="flex items-center justify-between mb-2" data-id="element-16828">
                  <div className="flex items-center gap-1.5" data-id="element-16829">
                    <span className={`text-lg ${sign.best ? 'text-rose-500' : 'text-slate-400'}`} data-id="element-16830">
                      {sign.symbol}
                    </span>
                    <span className="font-bold text-slate-800 text-sm" data-id="element-16831">
                      {sign.sign}
                    </span>
                  </div>
                  <span className={`text-xs font-bold ${sign.percent >= 80 ? 'text-emerald-600' : sign.percent >= 70 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-16832">
                    {sign.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mb-2 overflow-hidden" data-id="element-16833">
                  <div className={`h-1.5 rounded-full ${sign.percent >= 80 ? 'bg-emerald-500' : sign.percent >= 70 ? 'bg-amber-500' : 'bg-slate-400'}`} style={{
                width: `${sign.percent}%`
              }} data-id="element-16834" />
                </div>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-16835">
                  {sign.note}
                </p>
              </div>)}
          </div>
        </div>

        {/* 13. КАРМИЧЕСКИЕ ЗАДАЧИ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16836">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-16838" />} title="Кармические задачи" subtitle="Предназначение и уроки души" color="purple" data-id="element-16837" />
          <div className="space-y-6" data-id="element-16839">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-16840">
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-200" data-id="element-16841">
                <h3 className="font-bold text-purple-800 mb-2 flex items-center gap-2" data-id="element-16842">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-16843" /> Вектор развития
                  (Северный узел)
                </h3>
                {renderParagraphs(KARMIC_SECTION.northNode)}
              </div>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200" data-id="element-16844">
                <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-16845">
                  <AlertTriangleIcon className="w-4 h-4" data-id="element-16846" /> Прошлый опыт (Южный
                  узел)
                </h3>
                {renderParagraphs(KARMIC_SECTION.southNode)}
              </div>
            </div>

            <div data-id="element-16847">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-16848">
                Главный урок Сатурна
              </h3>
              {renderParagraphs(KARMIC_SECTION.saturnLesson)}
            </div>

            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg" data-id="element-16849">
              <h3 className="font-bold text-white mb-2 text-lg" data-id="element-16850">
                Жизненная миссия
              </h3>
              <p className="text-indigo-100 leading-relaxed" data-id="element-16851">
                {KARMIC_SECTION.lifeMission}
              </p>
            </div>

            <div className="mt-8" data-id="element-16852">
              <h3 className="font-bold text-slate-800 mb-4" data-id="element-16853">Главные вызовы</h3>
              <div className="space-y-3" data-id="element-16854">
                {CHALLENGES_SECTION.mainChallenges.split('\n\n').filter(p => !p.startsWith('Каждая')).map((challenge, i) => <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-rose-100" data-id="element-16855">
                      <div className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-16856">
                        {i + 1}
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-16857">
                        {challenge}
                      </p>
                    </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* 14. ТАЛАНТЫ И ПРЕДНАЗНАЧЕНИЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-16858">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-16860" />} title="Таланты и здоровье" subtitle="Ваши сильные стороны и физический потенциал" color="amber" data-id="element-16859" />

          <div className="space-y-8" data-id="element-16861">
            <div data-id="element-16862">
              <h3 className="font-bold text-slate-800 mb-4" data-id="element-16863">
                Природные способности
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-16864">
                {TALENTS_SECTION.naturalAbilities.split('\n\n').filter(p => !p.startsWith('На основе')).map((talent, i) => <div key={i} className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm" data-id="element-16865">
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-16866">
                        {talent}
                      </p>
                    </div>)}
              </div>
            </div>

            <div data-id="element-16867">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-16868">
                <ActivityIcon className="w-5 h-5 text-emerald-500" data-id="element-16869" />{' '}
                Астрологическое здоровье
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-16870">
                {HEALTH_SECTION.overview}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-16871">
                <Card padding="md" className="bg-emerald-50/50 border-emerald-100" data-id="element-16872">
                  <h4 className="font-bold text-emerald-800 mb-2 text-sm" data-id="element-16873">
                    Витальность (Солнце)
                  </h4>
                  <p className="text-xs text-slate-600" data-id="element-16874">
                    {HEALTH_SECTION.sunHealth}
                  </p>
                </Card>
                <Card padding="md" className="bg-indigo-50/50 border-indigo-100" data-id="element-16875">
                  <h4 className="font-bold text-indigo-800 mb-2 text-sm" data-id="element-16876">
                    Психосоматика (Луна)
                  </h4>
                  <p className="text-xs text-slate-600" data-id="element-16877">
                    {HEALTH_SECTION.moonHealth}
                  </p>
                </Card>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-16878">
                <h4 className="font-bold text-slate-800 mb-3 text-sm" data-id="element-16879">
                  Рекомендации по здоровью
                </h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-16880">
                  {HEALTH_SECTION.recommendations.split('\n').map((rec, i) => <li key={i} className="flex items-start gap-2 text-xs text-slate-600" data-id="element-16881">
                      <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-emerald-500 flex-shrink-0" data-id="element-16882" />
                      {rec.replace('•', '').trim()}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 15. ИТОГОВЫЙ АНАЛИЗ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16883">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-16885" />} title="Итоговый анализ" subtitle="Синтез натальной карты" color="indigo" data-id="element-16884" />

          <div className="space-y-6 mb-8" data-id="element-16886">
            {renderParagraphs(FINAL_ANALYSIS.summary)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-16887">
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200" data-id="element-16888">
              <h3 className="font-bold text-indigo-800 mb-3" data-id="element-16889">
                Ключевые качества
              </h3>
              <ul className="space-y-2" data-id="element-16890">
                {FINAL_ANALYSIS.keyQualities.map((q, i) => <li key={i} className="flex items-start gap-2 text-sm text-indigo-900" data-id="element-16891">
                    <StarIcon className="w-4 h-4 mt-0.5 text-indigo-500 flex-shrink-0" data-id="element-16892" />
                    {q}
                  </li>)}
              </ul>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200" data-id="element-16893">
              <h3 className="font-bold text-amber-800 mb-3" data-id="element-16894">
                Главные темы жизни
              </h3>
              <ul className="space-y-2" data-id="element-16895">
                {FINAL_ANALYSIS.lifeThemes.map((t, i) => <li key={i} className="flex items-start gap-2 text-sm text-amber-900" data-id="element-16896">
                    <CompassIcon className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-16897" />
                    {t}
                  </li>)}
              </ul>
            </div>
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-16898">
            Ваши персональные аффирмации
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-id="element-16899">
            {PERSONAL_AFFIRMATIONS.map((aff, i) => <div key={i} className={`p-4 rounded-xl border border-slate-200 bg-gradient-to-br ${aff.gradient}`} data-id="element-16900">
                <p className="font-medium text-slate-800 text-sm mb-2" data-id="element-16901">
                  «{aff.text}»
                </p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-16902">
                  {aff.source}
                </p>
              </div>)}
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-16903">
            Знаменитости с похожими показателями
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-16904">
            {CELEBRITY_MATCHES.map((celeb, i) => <div key={i} className="flex items-center gap-4 p-3 rounded-xl border border-slate-100 bg-slate-50" data-id="element-16905">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${celeb.color}`} data-id="element-16906">
                  {celeb.initials}
                </div>
                <div data-id="element-16907">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-16908">
                    {celeb.name}
                  </div>
                  <div className="text-xs text-indigo-600 font-medium mb-0.5" data-id="element-16909">
                    {celeb.sharedPlacement}
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-16910">{celeb.note}</div>
                </div>
              </div>)}
          </div>

          <div className="bg-gradient-to-br from-amber-100 via-yellow-50 to-amber-100 rounded-2xl p-6 sm:p-8 text-center shadow-sm border border-amber-200/60" data-id="element-16911">
            <SparklesIcon className="w-8 h-8 text-amber-500 mx-auto mb-4" data-id="element-16912" />
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-slate-800 italic max-w-2xl mx-auto" data-id="element-16913">
              {FINAL_ANALYSIS.finalAdvice}
            </p>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER SOLAR CONTENT
  // ═══════════════════════════════════════════
  const renderSolarContent = () => {
    const planetIcons: Record<string, React.ReactNode> = {
      Меркурий: <MessageCircleIcon className="w-5 h-5" data-id="element-16914" />,
      Венера: <HeartIcon className="w-5 h-5" data-id="element-16915" />,
      Марс: <ZapIcon className="w-5 h-5" data-id="element-16916" />,
      Юпитер: <TrendingUpIcon className="w-5 h-5" data-id="element-16917" />,
      Сатурн: <ShieldIcon className="w-5 h-5" data-id="element-16918" />,
      Уран: <SparklesIcon className="w-5 h-5" data-id="element-16919" />,
      Нептун: <CompassIcon className="w-5 h-5" data-id="element-16920" />,
      Плутон: <ActivityIcon className="w-5 h-5" data-id="element-16921" />
    };
    const areaIcons: Record<string, React.ReactNode> = {
      briefcase: <BriefcaseIcon className="w-5 h-5" data-id="element-16922" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-16923" />,
      home: <StarIcon className="w-5 h-5" data-id="element-16924" />,
      'trending-up': <TrendingUpIcon className="w-5 h-5" data-id="element-16925" />,
      activity: <ActivityIcon className="w-5 h-5" data-id="element-16926" />,
      users: <UsersIcon className="w-5 h-5" data-id="element-16927" />
    };
    const areaColorMap: Record<string, {
      bg: string;
      border: string;
      text: string;
      iconBg: string;
    }> = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        text: 'text-emerald-700',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        text: 'text-rose-700',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-700',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-700',
        iconBg: 'bg-teal-100 text-teal-600'
      },
      sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-200',
        text: 'text-sky-700',
        iconBg: 'bg-sky-100 text-sky-600'
      }
    };
    // Helper: render a section header
    const SectionHeader = ({
      icon,
      title,
      subtitle
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-16928">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-16929">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-16930">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-16931">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-16932">{subtitle}</p>}
        </div>
      </div>;
    // Helper: render strengths/cautions cards
    const StrengthsCautions = ({
      strengths,
      cautions
    }: {
      strengths: string[];
      cautions: string[];
    }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-16933">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-16934">
          <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-16935">
            <ShieldIcon className="w-4 h-4" data-id="element-16936" /> Сильные стороны
          </h4>
          <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-16937">
            {strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-16938">
                <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-16939" />
                {s}
              </li>)}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-16940">
          <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-16941">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-16942" /> Зоны внимания
          </h4>
          <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-16943">
            {cautions.map((c, i) => <li key={i} className="flex items-start gap-2" data-id="element-16944">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-16945" />
                {c}
              </li>)}
          </ul>
        </div>
      </div>;
    // Individual planet sections (Mercury, Venus, Mars shown separately)
    const individualPlanets = SOLAR_PLANETS_IN_HOUSES.slice(0, 3);
    // Grouped planets (Jupiter, Saturn, outer)
    const groupedPlanets = SOLAR_PLANETS_IN_HOUSES.slice(3);
    return <>
        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16946">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-16948" />} title={SOLAR_INTRO.title} subtitle="Что такое соляр и как работает прогноз" data-id="element-16947" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-16949">
            <p data-id="element-16950">{SOLAR_INTRO.whatIsSolar}</p>
            <p data-id="element-16951">{SOLAR_INTRO.howItAffects}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ТЕМА ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/50 to-white" data-id="element-16952">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-16954" />} title="Общая тема года" subtitle="Ключевые энергии 2026 года" data-id="element-16953" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6" data-id="element-16955">
            <Card padding="md" className="bg-white border-amber-200" data-id="element-16956">
              <div className="flex items-center gap-2 mb-3" data-id="element-16957">
                <TargetIcon className="w-5 h-5 text-amber-500" data-id="element-16958" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-16959">
                  Тема года
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-16960">
                {SOLAR_YEAR_THEME.mainEnergy}
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-16961">
                Солнце и Сатурн в 10 доме
              </p>
            </Card>
            <Card padding="md" className="bg-white border-orange-200" data-id="element-16962">
              <div className="flex items-center gap-2 mb-3" data-id="element-16963">
                <BriefcaseIcon className="w-5 h-5 text-orange-500" data-id="element-16964" />
                <span className="text-xs font-bold text-orange-600 uppercase tracking-wider" data-id="element-16965">
                  Ключевая сфера
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-16966">
                Карьера и статус
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-16967">10 дом активирован</p>
            </Card>
            <Card padding="md" className="bg-white border-emerald-200" data-id="element-16968">
              <div className="flex items-center gap-2 mb-3" data-id="element-16969">
                <TrendingUpIcon className="w-5 h-5 text-emerald-500" data-id="element-16970" />
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-16971">
                  Лучший период
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-16972">Март — Май</div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-16973">
                Максимум возможностей
              </p>
            </Card>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-16974">
            <p data-id="element-16975">{SOLAR_YEAR_THEME.description}</p>
            <p data-id="element-16976">
              <strong className="text-slate-800" data-id="element-16977">Основные изменения:</strong>{' '}
              {SOLAR_YEAR_THEME.changes}
            </p>
            <p data-id="element-16978">
              <strong className="text-slate-800" data-id="element-16979">
                Психологическая атмосфера:
              </strong>{' '}
              {SOLAR_YEAR_THEME.atmosphere}
            </p>
            <p data-id="element-16980">
              <strong className="text-slate-800" data-id="element-16981">Точки роста:</strong>{' '}
              {SOLAR_YEAR_THEME.growthPoints}
            </p>
          </div>

          <div className="mt-6 bg-amber-100/50 rounded-xl p-4 border border-amber-200" data-id="element-16982">
            <h4 className="font-bold text-amber-800 mb-3" data-id="element-16983">
              Главные задачи года
            </h4>
            <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-16984">
              {SOLAR_YEAR_THEME.tasks.map((task, i) => <li key={i} className="flex items-start gap-2" data-id="element-16985">
                  <TargetIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-16986" />
                  {task}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 03. АСЦЕНДЕНТ СОЛЯРА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-16987">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-16989" />} title={SOLAR_ASCENDANT.title} subtitle={`Восходящий знак: ${SOLAR_ASCENDANT.symbol} ${SOLAR_ASCENDANT.sign}`} data-id="element-16988" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-16990">
            <p data-id="element-16991">{SOLAR_ASCENDANT.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 my-6" data-id="element-16992">
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-16993">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-16994">
                  Проявление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-16995">
                  {SOLAR_ASCENDANT.manifestation}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-amber-200" data-id="element-16996">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-16997">
                  Стратегия
                </h4>
                <p className="text-sm text-slate-600" data-id="element-16998">
                  {SOLAR_ASCENDANT.strategy}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-emerald-200" data-id="element-16999">
                <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-17000">
                  Восприятие
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17001">
                  {SOLAR_ASCENDANT.perception}
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* ══════════ 04. СОЛНЦЕ В СОЛЯРЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-17002">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17004" />} title={SOLAR_SUN_POSITION.title} subtitle={`${SOLAR_SUN_POSITION.sign} • ${SOLAR_SUN_POSITION.house}-й дом`} data-id="element-17003" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17005">
            <p data-id="element-17006">{SOLAR_SUN_POSITION.description}</p>
            <p data-id="element-17007">
              <strong className="text-slate-800" data-id="element-17008">Ключевая сфера:</strong>{' '}
              {SOLAR_SUN_POSITION.keySphere}
            </p>
            <p data-id="element-17009">{SOLAR_SUN_POSITION.focus}</p>
          </div>

          <div className="mt-6 bg-white rounded-xl p-4 border border-amber-200" data-id="element-17010">
            <h4 className="font-bold text-amber-800 mb-3" data-id="element-17011">Возможные события</h4>
            <ul className="space-y-1.5 text-sm text-slate-700" data-id="element-17012">
              {SOLAR_SUN_POSITION.events.map((event, i) => <li key={i} className="flex items-start gap-2" data-id="element-17013">
                  <SunIcon className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-17014" />
                  {event}
                </li>)}
            </ul>
          </div>

          <StrengthsCautions strengths={SOLAR_SUN_POSITION.strengths} cautions={SOLAR_SUN_POSITION.cautions} data-id="element-17015" />
        </div>

        {/* ══════════ 05. ЛУНА В СОЛЯРЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17016">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-17018" />} title={SOLAR_MOON.title} subtitle={`${SOLAR_MOON.sign} • ${SOLAR_MOON.house}-й дом`} data-id="element-17017" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17019">
            <p data-id="element-17020">{SOLAR_MOON.description}</p>
            <p data-id="element-17021">
              <strong className="text-slate-800" data-id="element-17022">Эмоциональный фон:</strong>{' '}
              {SOLAR_MOON.emotions}
            </p>
            <p data-id="element-17023">
              <strong className="text-slate-800" data-id="element-17024">
                Внутренние потребности:
              </strong>{' '}
              {SOLAR_MOON.needs}
            </p>
            <p data-id="element-17025">
              <strong className="text-slate-800" data-id="element-17026">Отношения с близкими:</strong>{' '}
              {SOLAR_MOON.relations}
            </p>
          </div>

          <StrengthsCautions strengths={SOLAR_MOON.strengths} cautions={SOLAR_MOON.cautions} data-id="element-17027" />
        </div>

        {/* ══════════ 06-08. INDIVIDUAL PLANETS (Mercury, Venus, Mars) ══════════ */}
        {individualPlanets.map((planet, idx) => <div key={idx} className={`p-4 sm:p-6 md:p-8 border-b border-slate-100 ${idx % 2 === 0 ? 'bg-gradient-to-br from-slate-50/50 to-white' : ''}`} data-id="element-17028">
            <SectionHeader icon={planetIcons[planet.planet] || <StarIcon className="w-5 h-5" data-id="element-17030" />} title={planet.title} subtitle={`${planet.symbol} ${planet.sign} • ${planet.house}-й дом`} data-id="element-17029" />
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-17031">
              {planet.description}
            </p>
            <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-17032">
              <h4 className="font-bold text-slate-800 mb-3" data-id="element-17033">
                Возможные события
              </h4>
              <ul className="space-y-1.5 text-sm text-slate-700" data-id="element-17034">
                {planet.events.map((event, i) => <li key={i} className="flex items-start gap-2" data-id="element-17035">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-17036" />
                    {event}
                  </li>)}
              </ul>
            </div>
          </div>)}

        {/* ══════════ 09. ЮПИТЕР, САТУРН И ВНЕШНИЕ ПЛАНЕТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17037">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-17039" />} title="Юпитер, Сатурн и внешние планеты" subtitle="Долгосрочные тенденции года" data-id="element-17038" />

          <div className="space-y-6" data-id="element-17040">
            {groupedPlanets.map((planet, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-17041">
                <div className="flex items-center gap-3 mb-3" data-id="element-17042">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-lg" data-id="element-17043">
                    {planet.symbol}
                  </div>
                  <div data-id="element-17044">
                    <h4 className="font-bold text-slate-900" data-id="element-17045">{planet.title}</h4>
                    <p className="text-xs text-slate-500" data-id="element-17046">
                      {planet.sign} • {planet.house}-й дом
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-17047">
                  {planet.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-17048">
                  {planet.events.map((event, i) => <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full border border-indigo-100" data-id="element-17049">
                      {event}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 10. АСПЕКТЫ СОЛЯРА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17050">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17052" />} title="Аспекты соляра" subtitle="Взаимодействие планетарных энергий года" color="amber" data-id="element-17051" />
          <div className="space-y-4" data-id="element-17053">
            {COMBO_SOLAR_ASPECTS.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.1
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-17054">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-17055">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-17056">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-17057">
                        {aspect.aspect}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-17058">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : 'warning'} className="text-xs" data-id="element-17059">
                      {isHarmonious ? 'Гармоничный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17060">
                    {aspect.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 11-13. СФЕРЫ ЖИЗНИ ══════════ */}
        {SOLAR_LIFE_AREAS.map((area, idx) => {
        const colors = areaColorMap[area.color] || areaColorMap.emerald;
        // Group: 11 = career+relationships, 12 = family, 13 = growth+health+social
        // Show section headers for grouping
        const showCareerHeader = idx === 0;
        const showRelationsHeader = idx === 1;
        const showGrowthHeader = idx === 3;
        return <Fragment key={area.id} data-id="element-17061">
              {showCareerHeader && <div className="p-4 sm:p-6 md:p-8 pb-0 border-b-0" data-id="element-17062">
                  <SectionHeader icon={<BriefcaseIcon className="w-5 h-5" data-id="element-17064" />} title="Ключевые сферы жизни года" subtitle="Подробный разбор каждой области" data-id="element-17063" />
                </div>}
              <div className={`p-4 sm:p-6 md:p-8 pb-6 ${idx === SOLAR_LIFE_AREAS.length - 1 ? 'border-b border-slate-100' : ''}`} data-id="element-17065">
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-17066">
                  <div className="flex items-center gap-3 mb-3" data-id="element-17067">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-17068">
                      {areaIcons[area.icon] || <StarIcon className="w-4 h-4" data-id="element-17069" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-17070">{area.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17071">
                    {area.analysis}
                  </p>
                </motion.div>
              </div>
            </Fragment>;
      })}

        {/* ══════════ 14. ВАЖНЫЕ ПЕРИОДЫ ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17072">
          <SectionHeader icon={<CalendarIcon className="w-5 h-5" data-id="element-17074" />} title="Важные периоды года" subtitle="Помесячный прогноз активности" data-id="element-17073" />

          {/* Monthly Forecast Chart */}
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5 sm:gap-2 mb-6" data-id="element-17075">
            {SOLAR_MONTHLY_FORECAST.map((month, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className="text-center" data-id="element-17076">
                <div className="text-[10px] text-slate-400 mb-1" data-id="element-17077">
                  {month.month}
                </div>
                <div className="relative h-20 bg-slate-100 rounded-full overflow-hidden" data-id="element-17078">
                  <motion.div className={`absolute bottom-0 w-full rounded-full ${month.intensity >= 80 ? 'bg-amber-500' : month.intensity >= 60 ? 'bg-amber-400' : 'bg-amber-300'}`} initial={{
                height: 0
              }} animate={{
                height: `${month.intensity}%`
              }} transition={{
                duration: 0.8,
                delay: i * 0.05
              }} data-id="element-17079" />
                </div>
                <div className="text-[9px] text-slate-400 mt-1 truncate" data-id="element-17080">
                  {month.theme}
                </div>
              </motion.div>)}
          </div>
          <div className="flex justify-center gap-4 mb-8 text-xs text-slate-500" data-id="element-17081">
            <span className="flex items-center gap-1" data-id="element-17082">
              <span className="w-3 h-3 rounded-full bg-amber-500" data-id="element-17083"></span>{' '}
              Высокая активность
            </span>
            <span className="flex items-center gap-1" data-id="element-17084">
              <span className="w-3 h-3 rounded-full bg-amber-300" data-id="element-17085"></span>{' '}
              Умеренная
            </span>
          </div>

          {/* Key Dates Grid */}
          <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17086">
            Ключевые даты года
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-17087">
            {SOLAR_KEY_DATES.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.1
          }} className={`rounded-xl p-3 border ${item.type === 'positive' ? 'bg-emerald-50 border-emerald-200' : item.type === 'caution' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-17088">
                <div className={`text-sm font-bold ${item.type === 'positive' ? 'text-emerald-700' : item.type === 'caution' ? 'text-amber-700' : 'text-slate-700'}`} data-id="element-17089">
                  {item.date}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-17090">{item.event}</div>
              </motion.div>)}
          </div>

          {/* Sample Month Deep Dive */}
          <div className="mt-8" data-id="element-17091">
            <div className="flex items-center gap-3 mb-4" data-id="element-17092">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-17093">
                <CalendarIcon className="w-4 h-4" data-id="element-17094" />
              </div>
              <div data-id="element-17095">
                <h3 className="font-bold text-slate-900" data-id="element-17096">Март 2026 — Прорыв</h3>
                <p className="text-xs text-slate-500" data-id="element-17097">
                  Самый активный месяц года
                </p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-17098">
              Март станет поворотным месяцем. Новолуние в вашем 10 доме 15 марта
              запускает новый карьерный цикл. Это идеальное время для запуска
              проектов, переговоров о повышении или смены работы. Юпитер в
              тригоне к натальному Солнцу усиливает вашу уверенность и
              притягивает удачу.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-17099">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17100">
                <h4 className="font-bold text-emerald-800 mb-3" data-id="element-17101">
                  Благоприятно
                </h4>
                <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17102">
                  <li className="flex items-start gap-2" data-id="element-17103">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17104" />
                    Карьерные переговоры
                  </li>
                  <li className="flex items-start gap-2" data-id="element-17105">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17106" />
                    Запуск проектов
                  </li>
                  <li className="flex items-start gap-2" data-id="element-17107">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17108" />
                    Публичные выступления
                  </li>
                </ul>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17109">
                <h4 className="font-bold text-amber-800 mb-3" data-id="element-17110">Осторожно</h4>
                <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-17111">
                  <li className="flex items-start gap-2" data-id="element-17112">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17113" />
                    Импульсивные решения
                  </li>
                  <li className="flex items-start gap-2" data-id="element-17114">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17115" />
                    Переоценка сил
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════ 15. РЕКОМЕНДАЦИИ НА ГОД ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-17116">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-17118" />} title="Рекомендации на год" subtitle="Практические советы по использованию энергий" data-id="element-17117" />

          <div className="space-y-4" data-id="element-17119">
            {SOLAR_RECOMMENDATIONS.map((rec, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-5 border border-amber-200" data-id="element-17120">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-17121">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold" data-id="element-17122">
                    {i + 1}
                  </span>
                  {rec.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17123">
                  {rec.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 16. ИТОГ ПРОГНОЗА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17124">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-17126" />} title="Итог прогноза" subtitle="Резюме вашего солярного года" data-id="element-17125" />

          <div className="space-y-4 text-slate-600 leading-relaxed mb-6" data-id="element-17127">
            <p data-id="element-17128">{SOLAR_SUMMARY.overview}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-17129">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-17130">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17131">
                <TrendingUpIcon className="w-4 h-4" data-id="element-17132" /> Главные возможности
              </h4>
              <p className="text-sm text-emerald-700 leading-relaxed" data-id="element-17133">
                {SOLAR_SUMMARY.opportunities}
              </p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-17134">
              <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2" data-id="element-17135">
                <BookOpenIcon className="w-4 h-4" data-id="element-17136" /> Ключевые уроки
              </h4>
              <p className="text-sm text-indigo-700 leading-relaxed" data-id="element-17137">
                {SOLAR_SUMMARY.lessons}
              </p>
            </div>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER COMPATIBILITY CONTENT
  // ═══════════════════════════════════════════
  const renderCompatibilityContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-17138">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-17139">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-17140">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-17141">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-17142">{subtitle}</p>}
        </div>
      </div>;
    const StrengthsCautions = ({
      strengths,
      cautions,
      strengthsTitle,
      cautionsTitle
    }: {
      strengths: string[];
      cautions: string[];
      strengthsTitle?: string;
      cautionsTitle?: string;
    }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-17143">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17144">
          <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17145">
            <ShieldIcon className="w-4 h-4" data-id="element-17146" />{' '}
            {strengthsTitle || 'Сильные стороны'}
          </h4>
          <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17147">
            {strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-17148">
                <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17149" />
                {s}
              </li>)}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17150">
          <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-17151">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-17152" />{' '}
            {cautionsTitle || 'Зоны внимания'}
          </h4>
          <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-17153">
            {cautions.map((c, i) => <li key={i} className="flex items-start gap-2" data-id="element-17154">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17155" />
                {c}
              </li>)}
          </ul>
        </div>
      </div>;
    const houseIconMap: Record<string, React.ReactNode> = {
      sun: <SunIcon className="w-5 h-5" data-id="element-17156" />,
      moon: <MoonIcon className="w-5 h-5" data-id="element-17157" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-17158" />,
      zap: <ZapIcon className="w-5 h-5" data-id="element-17159" />,
      sparkles: <SparklesIcon className="w-5 h-5" data-id="element-17160" />,
      compass: <CompassIcon className="w-5 h-5" data-id="element-17161" />,
      home: <StarIcon className="w-5 h-5" data-id="element-17162" />
    };
    const houseColorMap: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        iconBg: 'bg-orange-100 text-orange-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100 text-purple-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        iconBg: 'bg-pink-100 text-pink-600'
      }
    };
    const strengthIconMap: Record<string, React.ReactNode> = {
      heart: <HeartIcon className="w-4 h-4" data-id="element-17163" />,
      sparkles: <SparklesIcon className="w-4 h-4" data-id="element-17164" />,
      zap: <ZapIcon className="w-4 h-4" data-id="element-17165" />,
      home: <StarIcon className="w-4 h-4" data-id="element-17166" />,
      compass: <CompassIcon className="w-4 h-4" data-id="element-17167" />
    };
    const strengthColorMap: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        iconBg: 'bg-pink-100 text-pink-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100 text-purple-600'
      }
    };
    return <>
        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17168">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-17170" />} title={COMPAT_INTRO.title} subtitle="Что такое синастрия и как работает анализ" data-id="element-17169" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17171">
            <p data-id="element-17172">{COMPAT_INTRO.whatIsSynastry}</p>
            <p data-id="element-17173">{COMPAT_INTRO.howItWorks}</p>
            <p data-id="element-17174">{COMPAT_INTRO.whatIsAnalyzed}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ДИНАМИКА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/50 to-white" data-id="element-17175">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-17177" />} title={COMPAT_OVERALL_DYNAMICS.title} subtitle="Энергетика и потенциал вашего союза" data-id="element-17176" />

          {/* Compatibility Scores */}
          <div className="space-y-4 mb-6" data-id="element-17178">
            {COMPATIBILITY_SCORES.map((item, i) => <div key={i} data-id="element-17179">
                <div className="flex justify-between text-sm mb-1.5" data-id="element-17180">
                  <span className="font-medium text-slate-700" data-id="element-17181">
                    {item.area}
                  </span>
                  <span className={`font-bold ${item.percent >= 80 ? 'text-emerald-600' : item.percent >= 60 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-17182">
                    {item.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-17183">
                  <motion.div className={`${item.color} h-3 rounded-full`} initial={{
                width: 0
              }} animate={{
                width: `${item.percent}%`
              }} transition={{
                duration: 1,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-17184" />
                </div>
              </div>)}
          </div>

          <div className="bg-rose-100/50 rounded-xl p-4 border border-rose-200 mb-6" data-id="element-17185">
            <div className="text-center" data-id="element-17186">
              <div className="text-3xl font-bold text-rose-600 mb-1" data-id="element-17187">83%</div>
              <div className="text-sm text-rose-700" data-id="element-17188">
                Общий индекс совместимости
              </div>
            </div>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17189">
            <p data-id="element-17190">{COMPAT_OVERALL_DYNAMICS.overallEnergy}</p>
            <p data-id="element-17191">{COMPAT_OVERALL_DYNAMICS.potential}</p>
          </div>

          <StrengthsCautions strengths={COMPAT_OVERALL_DYNAMICS.strengths} cautions={COMPAT_OVERALL_DYNAMICS.challenges} cautionsTitle="Ключевые вызовы" data-id="element-17192" />
        </div>

        {/* ══════════ 03. СОЛНЦЕ И ЛИЧНОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17193">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17195" />} title={COMPAT_SUN_INTERACTION.title} subtitle={COMPAT_SUN_INTERACTION.subtitle} data-id="element-17194" />

          {/* Two People's Big Three */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6" data-id="element-17196">
            <div className="bg-rose-50/50 rounded-xl p-4 border border-rose-100" data-id="element-17197">
              <div className="text-sm font-bold text-rose-600 mb-3" data-id="element-17198">
                Алексей
              </div>
              <div className="space-y-2" data-id="element-17199">
                <div className="flex justify-between text-sm" data-id="element-17200">
                  <span className="text-slate-500" data-id="element-17201">☉ Солнце</span>
                  <span className="font-medium text-slate-700" data-id="element-17202">♌ Лев</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17203">
                  <span className="text-slate-500" data-id="element-17204">☽ Луна</span>
                  <span className="font-medium text-slate-700" data-id="element-17205">
                    ♏ Скорпион
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17206">
                  <span className="text-slate-500" data-id="element-17207">ASC</span>
                  <span className="font-medium text-slate-700" data-id="element-17208">♐ Стрелец</span>
                </div>
              </div>
            </div>
            <div className="bg-sky-50/50 rounded-xl p-4 border border-sky-100" data-id="element-17209">
              <div className="text-sm font-bold text-sky-600 mb-3" data-id="element-17210">Мария</div>
              <div className="space-y-2" data-id="element-17211">
                <div className="flex justify-between text-sm" data-id="element-17212">
                  <span className="text-slate-500" data-id="element-17213">☉ Солнце</span>
                  <span className="font-medium text-slate-700" data-id="element-17214">♓ Рыбы</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17215">
                  <span className="text-slate-500" data-id="element-17216">☽ Луна</span>
                  <span className="font-medium text-slate-700" data-id="element-17217">♋ Рак</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17218">
                  <span className="text-slate-500" data-id="element-17219">ASC</span>
                  <span className="font-medium text-slate-700" data-id="element-17220">♎ Весы</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17221">
            <p data-id="element-17222">{COMPAT_SUN_INTERACTION.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-17223">
              <Card padding="md" className="bg-white border-amber-200" data-id="element-17224">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-17225">
                  Алексей (♌ Лев)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17226">
                  {COMPAT_SUN_INTERACTION.partner1Goals}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-17227">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-17228">
                  Мария (♓ Рыбы)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17229">
                  {COMPAT_SUN_INTERACTION.partner2Goals}
                </p>
              </Card>
            </div>
            <p data-id="element-17230">
              <strong className="text-slate-800" data-id="element-17231">Взаимное понимание:</strong>{' '}
              {COMPAT_SUN_INTERACTION.mutualUnderstanding}
            </p>
          </div>
        </div>

        {/* ══════════ 04. ЭМОЦИОНАЛЬНАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17232">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-17234" />} title={COMPAT_EMOTIONAL.title} subtitle={COMPAT_EMOTIONAL.subtitle} data-id="element-17233" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17235">
            <p data-id="element-17236">{COMPAT_EMOTIONAL.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-17237">
              <Card padding="md" className="bg-white border-rose-200" data-id="element-17238">
                <h4 className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2" data-id="element-17239">
                  Алексей (☽ Скорпион)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17240">
                  {COMPAT_EMOTIONAL.partner1Needs}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-sky-200" data-id="element-17241">
                <h4 className="text-xs font-bold text-sky-600 uppercase tracking-wider mb-2" data-id="element-17242">
                  Мария (☽ Рак)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17243">
                  {COMPAT_EMOTIONAL.partner2Needs}
                </p>
              </Card>
            </div>

            <p data-id="element-17244">
              <strong className="text-slate-800" data-id="element-17245">
                Эмоциональная поддержка:
              </strong>{' '}
              {COMPAT_EMOTIONAL.emotionalSupport}
            </p>
          </div>

          <StrengthsCautions strengths={COMPAT_EMOTIONAL.strengths} cautions={COMPAT_EMOTIONAL.cautions} data-id="element-17246" />
        </div>

        {/* ══════════ 05. РОМАНТИЧЕСКАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17247">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-17249" />} title={COMPAT_ROMANTIC.title} subtitle={COMPAT_ROMANTIC.subtitle} data-id="element-17248" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17250">
            <p data-id="element-17251">{COMPAT_ROMANTIC.description}</p>
            <p data-id="element-17252">
              <strong className="text-slate-800" data-id="element-17253">Романтическая химия:</strong>{' '}
              {COMPAT_ROMANTIC.chemistry}
            </p>
            <p data-id="element-17254">{COMPAT_ROMANTIC.loveStyles}</p>
          </div>

          <StrengthsCautions strengths={COMPAT_ROMANTIC.strengths} cautions={COMPAT_ROMANTIC.cautions} data-id="element-17255" />
        </div>

        {/* ══════════ 06. ИНТЕЛЛЕКТУАЛЬНАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-sky-50/30 to-white" data-id="element-17256">
          <SectionHeader icon={<MessageCircleIcon className="w-5 h-5" data-id="element-17258" />} title={COMPAT_INTELLECTUAL.title} subtitle={COMPAT_INTELLECTUAL.subtitle} data-id="element-17257" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17259">
            <p data-id="element-17260">{COMPAT_INTELLECTUAL.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-17261">
              <Card padding="md" className="bg-white border-amber-200" data-id="element-17262">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-17263">
                  Стиль Алексея (☿ Лев)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17264">
                  {COMPAT_INTELLECTUAL.partner1Style}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-17265">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-17266">
                  Стиль Марии (☿ Рыбы)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17267">
                  {COMPAT_INTELLECTUAL.partner2Style}
                </p>
              </Card>
            </div>

            <p data-id="element-17268">{COMPAT_INTELLECTUAL.communicationDynamics}</p>
          </div>

          <div className="mt-6 bg-amber-50 rounded-xl p-4 border border-amber-200" data-id="element-17269">
            <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-17270">
              <AlertTriangleIcon className="w-4 h-4" data-id="element-17271" /> Возможные сложности
            </h4>
            <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-17272">
              {COMPAT_INTELLECTUAL.difficulties.map((d, i) => <li key={i} className="flex items-start gap-2" data-id="element-17273">
                  <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17274" />
                  {d}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 07. КАРМИЧЕСКИЕ СВЯЗИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17275">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-17277" />} title={COMPAT_KARMIC.title} subtitle={COMPAT_KARMIC.subtitle} data-id="element-17276" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-17278">
            {COMPAT_KARMIC.description}
          </p>

          <div className="space-y-6" data-id="element-17279">
            {[COMPAT_KARMIC.saturnAnalysis, COMPAT_KARMIC.plutoAnalysis, COMPAT_KARMIC.nodesAnalysis].map((analysis, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-xl p-5 border border-purple-200" data-id="element-17280">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-17281">
                  <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold" data-id="element-17282">
                    {idx + 1}
                  </span>
                  {analysis.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17283">
                  {analysis.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 08. ПЛАНЕТЫ В ДОМАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-17284">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-17286" />} title="Планеты партнёра в домах другого" subtitle="Как вы влияете на жизнь друг друга" data-id="element-17285" />

          <div className="space-y-4" data-id="element-17287">
            {COMPAT_PLANETS_IN_HOUSES.map((item, idx) => {
            const colors = houseColorMap[item.color] || houseColorMap.amber;
            return <motion.div key={idx} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-17288">
                  <div className="flex items-center gap-3 mb-3" data-id="element-17289">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-17290">
                      {houseIconMap[item.icon] || <StarIcon className="w-4 h-4" data-id="element-17291" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-17292">{item.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17293">
                    {item.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 09. АСПЕКТЫ МЕЖДУ КАРТАМИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17294">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17296" />} title="Основные аспекты между картами" subtitle="Взаимодействие планетарных энергий" data-id="element-17295" />

          <div className="space-y-4" data-id="element-17297">
            {COMPAT_ASPECTS_DETAILED.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            const isIntense = aspect.nature === 'intense';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.08
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : isIntense ? 'bg-purple-50/50 border-purple-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-17298">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-17299">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-17300">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : isIntense ? 'text-purple-600' : 'text-rose-600'}`} data-id="element-17301">
                        {aspect.aspect}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-17302">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : isIntense ? 'pro' : 'warning'} className="text-xs" data-id="element-17303">
                      {isHarmonious ? 'Гармоничный' : isIntense ? 'Интенсивный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17304">
                    {aspect.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 10. СИЛЬНЫЕ СТОРОНЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-emerald-50/30 to-white" data-id="element-17305">
          <SectionHeader icon={<ShieldIcon className="w-5 h-5" data-id="element-17307" />} title={COMPAT_STRENGTHS.title} subtitle={COMPAT_STRENGTHS.description} data-id="element-17306" />

          <div className="space-y-4" data-id="element-17308">
            {COMPAT_STRENGTHS.items.map((item, idx) => {
            const colors = strengthColorMap[item.color] || strengthColorMap.rose;
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-17309">
                  <div className="flex items-center gap-3 mb-3" data-id="element-17310">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-17311">
                      {strengthIconMap[item.icon] || <StarIcon className="w-4 h-4" data-id="element-17312" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-17313">{item.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17314">
                    {item.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 11. ВОЗМОЖНЫЕ КОНФЛИКТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17315">
          <SectionHeader icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-17317" />} title={COMPAT_CONFLICTS.title} subtitle={COMPAT_CONFLICTS.description} data-id="element-17316" />

          <div className="space-y-4" data-id="element-17318">
            {COMPAT_CONFLICTS.items.map((item, idx) => {
            const severityColors = {
              high: {
                bg: 'bg-rose-50',
                border: 'border-rose-200',
                badge: 'warning' as const,
                label: 'Высокий'
              },
              medium: {
                bg: 'bg-amber-50',
                border: 'border-amber-200',
                badge: 'neutral' as const,
                label: 'Средний'
              },
              low: {
                bg: 'bg-slate-50',
                border: 'border-slate-200',
                badge: 'success' as const,
                label: 'Низкий'
              }
            };
            const sev = severityColors[item.severity];
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className={`${sev.bg} rounded-xl p-5 border ${sev.border}`} data-id="element-17319">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-17320">
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-17321">
                      {item.title}
                    </h4>
                    <Badge variant={sev.badge} className="text-xs" data-id="element-17322">
                      Риск: {sev.label}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-17323">
                    {item.text}
                  </p>
                  <div className="bg-white/60 rounded-lg p-3 border border-white/80" data-id="element-17324">
                    <p className="text-xs text-slate-700" data-id="element-17325">
                      <strong className="text-emerald-700" data-id="element-17326">Решение:</strong>{' '}
                      {item.solution}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 12. ДОЛГОСРОЧНЫЙ ПОТЕНЦИАЛ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-purple-50/30 to-white" data-id="element-17327">
          <SectionHeader icon={<TrendingUpIcon className="w-5 h-5" data-id="element-17329" />} title={COMPAT_LONGTERM.title} subtitle={`Общий потенциал: ${COMPAT_LONGTERM.overallScore}%`} data-id="element-17328" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17330">
            <p data-id="element-17331">{COMPAT_LONGTERM.description}</p>
            <p data-id="element-17332">
              <strong className="text-slate-800" data-id="element-17333">Устойчивость союза:</strong>{' '}
              {COMPAT_LONGTERM.sustainability}
            </p>
            <p data-id="element-17334">
              <strong className="text-slate-800" data-id="element-17335">Совместные цели:</strong>{' '}
              {COMPAT_LONGTERM.sharedGoals}
            </p>
          </div>

          <div className="mt-6 bg-purple-100/50 rounded-xl p-4 border border-purple-200" data-id="element-17336">
            <h4 className="font-bold text-purple-800 mb-3" data-id="element-17337">
              Направления развития отношений
            </h4>
            <ul className="space-y-1.5 text-sm text-purple-700" data-id="element-17338">
              {COMPAT_LONGTERM.developmentAreas.map((area, i) => <li key={i} className="flex items-start gap-2" data-id="element-17339">
                  <TrendingUpIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17340" />
                  {area}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 13. РЕКОМЕНДАЦИИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17341">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-17343" />} title="Практические рекомендации" subtitle="Советы для укрепления отношений" data-id="element-17342" />

          <div className="space-y-4" data-id="element-17344">
            {COMPAT_RECOMMENDATIONS.map((rec, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-5 border border-rose-200" data-id="element-17345">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-17346">
                  <span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-bold" data-id="element-17347">
                    {i + 1}
                  </span>
                  {rec.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17348">
                  {rec.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 14. ИТОГ АНАЛИЗА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-17349">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-17351" />} title="Итог анализа" subtitle="Резюме совместимости" data-id="element-17350" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-17352">
            {COMPAT_SUMMARY.overview}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-17353">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-17354">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17355">
                <CheckIcon className="w-4 h-4" data-id="element-17356" /> Главные плюсы
              </h4>
              <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17357">
                {COMPAT_SUMMARY.mainPros.map((p, i) => <li key={i} className="flex items-start gap-2" data-id="element-17358">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17359" />
                    {p}
                  </li>)}
              </ul>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-17360">
              <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2" data-id="element-17361">
                <BookOpenIcon className="w-4 h-4" data-id="element-17362" /> Ключевые уроки
              </h4>
              <ul className="space-y-1.5 text-sm text-indigo-700" data-id="element-17363">
                {COMPAT_SUMMARY.keyLessons.map((l, i) => <li key={i} className="flex items-start gap-2" data-id="element-17364">
                    <CompassIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17365" />
                    {l}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER COMBO CONTENT
  // ═══════════════════════════════════════════
  const renderComboContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle,
      color = 'purple'
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-17366">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`} data-id="element-17367">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-17368">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-17369">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-17370">{subtitle}</p>}
        </div>
      </div>;
    const PartDivider = ({
      part,
      title,
      subtitle
    }: {
      part: number;
      title: string;
      subtitle: string;
    }) => <div className={`p-4 sm:p-6 md:p-8 border-b border-slate-100 ${part === 1 ? 'bg-gradient-to-r from-indigo-600 to-purple-600' : 'bg-gradient-to-r from-amber-600 to-orange-600'} text-white`} data-id="element-17371">
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-17372">
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/20 flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0" data-id="element-17373">
            {part}
          </div>
          <div className="min-w-0" data-id="element-17374">
            <h2 className="text-lg sm:text-2xl font-bold" data-id="element-17375">{title}</h2>
            <p className="text-white/70 text-xs sm:text-sm" data-id="element-17376">{subtitle}</p>
          </div>
        </div>
      </div>;
    const lifeAreaIcons: Record<string, React.ReactNode> = {
      briefcase: <BriefcaseIcon className="w-5 h-5" data-id="element-17377" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-17378" />,
      'trending-up': <TrendingUpIcon className="w-5 h-5" data-id="element-17379" />,
      compass: <CompassIcon className="w-5 h-5" data-id="element-17380" />,
      users: <UsersIcon className="w-5 h-5" data-id="element-17381" />,
      activity: <ActivityIcon className="w-5 h-5" data-id="element-17382" />
    };
    const lifeAreaColors: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        iconBg: 'bg-teal-100 text-teal-600'
      },
      sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-200',
        iconBg: 'bg-sky-100 text-sky-600'
      }
    };
    return <>
        {/* ═══════════════════════════════════ */}
        {/* PART 1 — NATAL CHART */}
        {/* ═══════════════════════════════════ */}
        <PartDivider part={1} title="Натальная карта" subtitle="Анализ личности, потенциала и жизненных особенностей" data-id="element-17383" />

        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17384">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-17386" />} title={COMBO_NATAL_INTRO.title} subtitle="Что такое натальная карта и что она показывает" color="indigo" data-id="element-17385" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17387">
            <p data-id="element-17388">{COMBO_NATAL_INTRO.whatIsNatalChart}</p>
            <p data-id="element-17389">{COMBO_NATAL_INTRO.whatItShows}</p>
            <p data-id="element-17390">{COMBO_NATAL_INTRO.whatIsAnalyzed}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ХАРАКТЕРИСТИКА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/50 to-white" data-id="element-17391">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17393" />} title={COMBO_PERSONALITY.title} subtitle="Базовые черты, мотивации и ценности" color="indigo" data-id="element-17392" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-17394">
            {COMBO_PERSONALITY.overview}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-17395">
            {COMBO_PERSONALITY.traits.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-4 border border-indigo-200" data-id="element-17396">
                <h4 className="font-bold text-indigo-800 mb-2" data-id="element-17397">{item.trait}</h4>
                <p className="text-sm text-slate-600" data-id="element-17398">{item.description}</p>
              </motion.div>)}
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17399">
            <p data-id="element-17400">
              <strong className="text-slate-800" data-id="element-17401">Мотивации:</strong>{' '}
              {COMBO_PERSONALITY.motivations}
            </p>
            <p data-id="element-17402">
              <strong className="text-slate-800" data-id="element-17403">Ценности:</strong>{' '}
              {COMBO_PERSONALITY.values}
            </p>
          </div>
        </div>

        {/* ══════════ 03. СОЛНЦЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17404">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17406" />} title={COMBO_SUN.title} subtitle={COMBO_SUN.subtitle} color="amber" data-id="element-17405" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17407">
            <p data-id="element-17408">{COMBO_SUN.description}</p>
            <p data-id="element-17409">
              <strong className="text-slate-800" data-id="element-17410">Жизненные цели:</strong>{' '}
              {COMBO_SUN.lifeGoals}
            </p>
            <p data-id="element-17411">
              <strong className="text-slate-800" data-id="element-17412">Проявление личности:</strong>{' '}
              {COMBO_SUN.personality}
            </p>
            <p data-id="element-17413">
              <strong className="text-slate-800" data-id="element-17414">Внутренний потенциал:</strong>{' '}
              {COMBO_SUN.potential}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-17415">
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17416">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17417">
                <ShieldIcon className="w-4 h-4" data-id="element-17418" /> Суперсилы
              </h4>
              <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17419">
                {COMBO_SUN.strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-17420">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17421" />
                    {s}
                  </li>)}
              </ul>
            </div>
            <div className="bg-rose-50 rounded-xl p-4 border border-rose-100" data-id="element-17422">
              <h4 className="font-bold text-rose-800 mb-3 flex items-center gap-2" data-id="element-17423">
                <ZapIcon className="w-4 h-4" data-id="element-17424" /> Теневая сторона
              </h4>
              <ul className="space-y-1.5 text-sm text-rose-700" data-id="element-17425">
                {COMBO_SUN.shadows.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-17426">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17427" />
                    {s}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* ══════════ 04. ЛУНА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17428">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-17430" />} title={COMBO_MOON.title} subtitle={COMBO_MOON.subtitle} color="indigo" data-id="element-17429" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17431">
            <p data-id="element-17432">{COMBO_MOON.description}</p>
            <p data-id="element-17433">
              <strong className="text-slate-800" data-id="element-17434">Эмоциональные реакции:</strong>{' '}
              {COMBO_MOON.emotionalReactions}
            </p>
            <p data-id="element-17435">
              <strong className="text-slate-800" data-id="element-17436">
                Внутренние потребности:
              </strong>{' '}
              {COMBO_MOON.innerNeeds}
            </p>
            <p data-id="element-17437">
              <strong className="text-slate-800" data-id="element-17438">
                Способ переживания чувств:
              </strong>{' '}
              {COMBO_MOON.feelingStyle}
            </p>
          </div>
        </div>

        {/* ══════════ 05. АСЦЕНДЕНТ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17439">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-17441" />} title={COMBO_ASCENDANT.title} subtitle={COMBO_ASCENDANT.subtitle} color="purple" data-id="element-17440" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17442">
            <p data-id="element-17443">{COMBO_ASCENDANT.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 my-6" data-id="element-17444">
              <Card padding="md" className="bg-white border-purple-200" data-id="element-17445">
                <h4 className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-2" data-id="element-17446">
                  Внешнее проявление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17447">
                  {COMBO_ASCENDANT.externalPresentation}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-amber-200" data-id="element-17448">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-17449">
                  Первое впечатление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17450">
                  {COMBO_ASCENDANT.firstImpression}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-emerald-200" data-id="element-17451">
                <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-17452">
                  Стиль поведения
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17453">
                  {COMBO_ASCENDANT.behaviorStyle}
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* ══════════ 06. ПЛАНЕТЫ В ЗНАКАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-purple-50/30 to-white" data-id="element-17454">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17456" />} title="Планеты в знаках" subtitle="Влияние каждой планеты на вашу личность" color="purple" data-id="element-17455" />
          <div className="space-y-4" data-id="element-17457">
            {COMBO_PLANETS_IN_SIGNS.map((planet, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.06
          }} className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-17458">
                <div className="flex items-center gap-3 mb-3" data-id="element-17459">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center text-lg" data-id="element-17460">
                    {planet.symbol}
                  </div>
                  <div data-id="element-17461">
                    <h4 className="font-bold text-slate-900" data-id="element-17462">
                      {planet.planet} в {planet.sign}
                    </h4>
                    <p className="text-xs text-slate-500" data-id="element-17463">
                      {planet.house}-й дом
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-17464">
                  {planet.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-17465">
                  {planet.strengths.map((s, i) => <span key={i} className="text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full border border-purple-100" data-id="element-17466">
                      {s}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 07. ПЛАНЕТЫ В ДОМАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17467">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-17469" />} title="Планеты в домах" subtitle="Активированные сферы жизни" color="indigo" data-id="element-17468" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-17470">
            {COMBO_HOUSES_SUMMARY.map((house, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.04
          }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-17471">
                <div className="flex items-center gap-2 mb-2" data-id="element-17472">
                  <span className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold" data-id="element-17473">
                    {house.house}
                  </span>
                  <div data-id="element-17474">
                    <span className="text-sm font-bold text-slate-900" data-id="element-17475">
                      {house.theme}
                    </span>
                    <span className="text-xs text-slate-400 ml-2" data-id="element-17476">
                      {house.sign}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-600" data-id="element-17477">{house.text}</p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 08. АСПЕКТЫ НАТАЛЬНОЙ КАРТЫ (COMBO) ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-17478">
          <div className="max-w-5xl mx-auto" data-id="element-17479">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-17480">
              <SparklesIcon className="w-5 h-5 text-indigo-500" data-id="element-17481" />
              Аспекты натальной карты
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-17482">
              {COMBO_NATAL_ASPECTS.map((aspect, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.03
            }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-17483">
                  <div className="flex items-center gap-2 mb-2" data-id="element-17484">
                    <span className="text-sm font-bold text-slate-900" data-id="element-17485">
                      {aspect.aspect}
                    </span>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-17486">
                      {aspect.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600" data-id="element-17487">{aspect.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 09. ЖИЗНЕННЫЕ СФЕРЫ (COMBO) ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-17488">
          <div className="max-w-5xl mx-auto" data-id="element-17489">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-17490">
              <TargetIcon className="w-5 h-5 text-emerald-500" data-id="element-17491" />
              Жизненные сферы
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-17492">
              {COMBO_NATAL_LIFE_AREAS.map((area, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: i * 0.05
            }} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-id="element-17493">
                  <h4 className="text-sm font-bold text-slate-900 mb-2" data-id="element-17494">
                    {area.area}
                  </h4>
                  <p className="text-xs text-slate-600 leading-relaxed" data-id="element-17495">
                    {area.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 10. СОЛЯР — ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t-4 border-amber-400" data-id="element-17496">
          <div className="max-w-5xl mx-auto text-center" data-id="element-17497">
            <Badge variant="warning" size="sm" className="mb-3" data-id="element-17498">
              Часть 2
            </Badge>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-17499">
              Солярный возврат
            </h2>
            <p className="text-sm text-slate-500 max-w-2xl mx-auto" data-id="element-17500">
              {COMBO_SOLAR_INTRO}
            </p>
          </div>
        </div>

        {/* ══════════ 11. ЭНЕРГИЯ ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-17501">
          <div className="max-w-5xl mx-auto" data-id="element-17502">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-17503">
              <SunIcon className="w-5 h-5 text-amber-500" data-id="element-17504" />
              Энергия года
            </h3>
            <div className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-17505">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17506">
                {COMBO_YEAR_ENERGY}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 12. СОЛЯР — АСЦЕНДЕНТ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-17507">
          <div className="max-w-5xl mx-auto" data-id="element-17508">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-17509">
              <CompassIcon className="w-5 h-5 text-indigo-500" data-id="element-17510" />
              Асцендент соляра
            </h3>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-17511">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17512">
                {COMBO_SOLAR_ASCENDANT}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 13. СОЛЯР — СОЛНЦЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-17513">
          <div className="max-w-5xl mx-auto" data-id="element-17514">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-17515">
              <SunIcon className="w-5 h-5 text-amber-500" data-id="element-17516" />
              Солнце в соляре
            </h3>
            <div className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-17517">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17518">
                {COMBO_SOLAR_SUN}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 14. СОЛЯР — ЛУНА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-17519">
          <div className="max-w-5xl mx-auto" data-id="element-17520">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-17521">
              <MoonIcon className="w-5 h-5 text-blue-500" data-id="element-17522" />
              Луна в соляре
            </h3>
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100" data-id="element-17523">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17524">
                {COMBO_SOLAR_MOON}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 15. СОЛЯР — ПЛАНЕТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-17525">
          <div className="max-w-5xl mx-auto" data-id="element-17526">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-17527">
              <StarIcon className="w-5 h-5 text-purple-500" data-id="element-17528" />
              Планеты в соляре
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-17529">
              {COMBO_SOLAR_PLANETS.map((planet, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.04
            }} className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-17530">
                  <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-17531">
                    {planet.planet}
                  </h4>
                  <p className="text-[10px] text-slate-400 mb-2" data-id="element-17532">
                    {planet.position}
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-17533">{planet.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 16. СОЛЯР — АСПЕКТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-17534">
          <div className="max-w-5xl mx-auto" data-id="element-17535">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-17536">
              <ActivityIcon className="w-5 h-5 text-rose-500" data-id="element-17537" />
              Аспекты соляра
            </h3>
            <div className="space-y-3" data-id="element-17538">
              {COMBO_SOLAR_ASPECTS.map((aspect, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.03
            }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-17539">
                  <div className="flex items-center gap-2 mb-1" data-id="element-17540">
                    <span className="text-sm font-bold text-slate-900" data-id="element-17541">
                      {aspect.aspect}
                    </span>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-17542">
                      {aspect.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600" data-id="element-17543">{aspect.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 17. КЛЮЧЕВЫЕ ПЕРИОДЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-17544">
          <div className="max-w-5xl mx-auto" data-id="element-17545">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-17546">
              <CalendarIcon className="w-5 h-5 text-emerald-500" data-id="element-17547" />
              Ключевые периоды года
            </h3>
            <div className="space-y-3" data-id="element-17548">
              {COMBO_KEY_PERIODS.map((period, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.05
            }} className="bg-white rounded-xl p-4 border border-slate-200 flex gap-4" data-id="element-17549">
                  <div className="w-16 shrink-0 text-center" data-id="element-17550">
                    <div className="text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg px-2 py-1" data-id="element-17551">
                      {period.period}
                    </div>
                  </div>
                  <div data-id="element-17552">
                    <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-17553">
                      {period.theme}
                    </h4>
                    <p className="text-xs text-slate-600" data-id="element-17554">{period.text}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 18. ВЗАИМОДЕЙСТВИЕ СОЛЯР + НАТАЛ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-17555">
          <div className="max-w-5xl mx-auto" data-id="element-17556">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-17557">
              <LayersIcon className="w-5 h-5 text-purple-500" data-id="element-17558" />
              Взаимодействие соляра и натала
            </h3>
            <div className="bg-purple-50 rounded-xl p-5 border border-purple-100" data-id="element-17559">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17560">
                {COMBO_SOLAR_NATAL_INTERACTION}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ ИТОГОВЫЙ АНАЛИЗ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-gradient-to-b from-indigo-50 to-white border-t border-indigo-100" data-id="element-17561">
          <div className="max-w-5xl mx-auto text-center" data-id="element-17562">
            <SparklesIcon className="w-8 h-8 text-indigo-500 mx-auto mb-3" data-id="element-17563" />
            <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-17564">
              Итоговый анализ
            </h2>
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm text-left" data-id="element-17565">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17566">
                Ваш комбо-анализ объединяет натальную карту и солярный возврат,
                создавая целостную картину вашей личности и предстоящего года.
                Используйте эти знания как компас для принятия решений и
                раскрытия своего потенциала.
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ FOOTER ══════════ */}
        <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-200" data-id="element-17567">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 max-w-5xl mx-auto" data-id="element-17568">
            <div className="text-center sm:text-left" data-id="element-17569">
              <p className="text-sm font-medium text-slate-700" data-id="element-17570">
                Astriqo • Premium отчёт
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-17571">
                Отчёт сгенерирован {new Date().toLocaleDateString('ru-RU')} •
                Все права защищены
              </p>
            </div>
            <button onClick={handlePrint} className="report-print-btn flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium" data-id="element-17572">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-17573">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-17574" />
                <polyline points="7 10 12 15 17 10" data-id="element-17575" />
                <line x1="12" y1="15" x2="12" y2="3" data-id="element-17576" />
              </svg>
              Скачать PDF
            </button>
          </div>
        </div>

        {/* ══════════ CTA BLOCK ══════════ */}
        <div className="p-6 sm:p-8 md:p-10 bg-white border-t border-slate-200 text-center print:hidden" data-id="element-17577">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3" data-id="element-17578">
            Хотите персональный разбор от профессионала?
          </h3>
          <p className="text-sm sm:text-base text-slate-500 mb-6 max-w-lg mx-auto" data-id="element-17579">
            Закажите индивидуальную консультацию с опытным астрологом для
            глубокого анализа вашей карты
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-17580">
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-id="element-17581">
              <UsersIcon className="w-4 h-4" data-id="element-17582" />
              Каталог астрологов
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors shadow-sm" data-id="element-17583">
              Заказать консультацию
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-17584">
                <line x1="5" y1="12" x2="19" y2="12" data-id="element-17585" />
                <polyline points="12 5 19 12 12 19" data-id="element-17586" />
              </svg>
            </button>
          </div>
        </div>

        {/* ══════════ REVIEW SECTION ══════════ */}
        <div className="p-6 sm:p-8 md:p-10 bg-slate-50 border-t border-slate-200 print:hidden" data-id="element-17587">
          <div className="max-w-lg mx-auto text-center" data-id="element-17588">
            {!reviewSubmitted ? <div data-id="element-17589">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4" data-id="element-17590">
                  <SparklesIcon className="w-6 h-6 text-amber-500" data-id="element-17591" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-17592">
                  Как вам отчёт?
                </h3>
                <p className="text-sm text-slate-500 mb-6" data-id="element-17593">
                  Ваш отзыв поможет улучшить качество наших отчётов
                </p>

                {/* Star Rating */}
                <div className="flex items-center justify-center gap-2 mb-6" data-id="element-17594">
                  {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setReviewRating(star)} className="transition-transform hover:scale-110" data-id="element-17595">
                      <StarIcon className={`w-8 h-8 transition-colors ${star <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-17596" />
                    </button>)}
                </div>

                {reviewRating > 0 && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-4" data-id="element-17597">
                    {/* Quick Tags */}
                    <div className="flex flex-wrap justify-center gap-2" data-id="element-17598">
                      {['Точность', 'Глубина', 'Оформление', 'Практичность', 'Персонализация', 'Читаемость'].map(tag => <button key={tag} onClick={() => setReviewTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${reviewTags.includes(tag) ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`} data-id="element-17599">
                          {tag}
                        </button>)}
                    </div>

                    {/* Text Area */}
                    <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Расскажите подробнее (необязательно)" rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 resize-none" data-id="element-17600" />

                    {/* Submit */}
                    <button onClick={() => setReviewSubmitted(true)} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm" data-id="element-17601">
                      Отправить отзыв
                    </button>

                    <p className="text-xs text-slate-400 mt-2" data-id="element-17602">
                      💎 Оставьте отзыв и получите скидку 10% на следующий отчёт
                    </p>
                  </motion.div>}
              </div> : <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} data-id="element-17603">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4" data-id="element-17604">
                  <CheckIcon className="w-6 h-6 text-emerald-600" data-id="element-17605" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-17606">
                  Спасибо за ваш отзыв!
                </h3>
                <p className="text-sm text-slate-500 mb-4" data-id="element-17607">
                  Скидка 10% уже применена к вашему аккаунту
                </p>
                <div className="flex items-center justify-center gap-1" data-id="element-17608">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`w-5 h-5 ${star <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-17609" />)}
                </div>
              </motion.div>}
          </div>
        </div>
      </>;
  };
}